import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["item", "placeholder"]
  static values = {
    url: String,
    group: String,
    sectionId: String,
    evaluationId: String,
    formId: String,
    promotionId: String
  }

  connect() {
    this.sortable = new Sortable(this.element, {
      group: this.groupValue || 'shared',
      animation: 150,
      handle: ".drag-handle",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onStart: this.start.bind(this),
      onEnd: this.end.bind(this)
    })
  }

  // Cleanup obligatoire (audit 2026-08-04, A5-F3) : SortableJS référence
  // l'élément dans son tableau global `sortables` — sans destroy(), chaque
  // navigation Turbo laisse un élément DOM détaché en mémoire.
  disconnect() {
    this.sortable?.destroy()
    this.sortable = null
  }

  start() {
    document.querySelectorAll("[data-sortable-placeholder-target='placeholder']").forEach(el => {
      el.classList.remove("hidden")
    })
  }

  end(event) {
    document.querySelectorAll("[data-sortable-placeholder-target='placeholder']").forEach(el => {
      el.classList.add("hidden")
    })

    const id = event.item.dataset.id
    // Convert 0-based index (SortableJS) to 1-based position (positioning gem)
    const newPosition = event.newIndex + 1
    const fromSectionId = event.from.dataset.sortableSectionIdValue
    const toSectionId = event.to.dataset.sortableSectionIdValue

    if (fromSectionId && toSectionId && fromSectionId !== toSectionId) {
      this.moveToSection(event.item, id, toSectionId, newPosition, fromSectionId)
    } else {
      this.reposition(id, newPosition)
    }
  }

  reposition(id, newPosition) {
    fetch(`${this.urlValue}/${id}/reposition`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({ position: newPosition })
    })
  }

  moveToSection(element, questionId, newSectionId, newPosition, oldSectionId) {
    const url = this.buildMoveUrl(questionId, oldSectionId)

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({
        new_section_id: newSectionId,
        position: newPosition
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Move failed')
      return response.json()
    })
    .then(data => {
      this.highlightElement(element)
      this.showToast(data.message, 'success')
      this.updateCounters(oldSectionId, newSectionId)
    })
    .catch(() => {
      this.rollback(element, oldSectionId)
      this.showToast("Erreur lors du déplacement de la question", 'error')
    })
  }

  buildMoveUrl(questionId, sectionId) {
    if (this.hasFormIdValue) {
      const promotionId = this.promotionIdValue || this.element.closest("[data-promotion-id]")?.dataset.promotionId
      const formId = this.formIdValue || this.element.closest("[data-form-id]")?.dataset.formId
      return `/promotions/${promotionId}/forms/${formId}/form_sections/${sectionId}/questions/${questionId}/move_to_section`
    } else {
      const evaluationId = this.evaluationIdValue || this.element.closest("[data-evaluation-id]")?.dataset.evaluationId
      return `/evaluations/${evaluationId}/evaluation_sections/${sectionId}/evaluation_questions/${questionId}/move_to_section`
    }
  }

  highlightElement(element) {
    element.classList.add("sortable-highlight")
    setTimeout(() => {
      element.classList.remove("sortable-highlight")
    }, 2000)
  }

  rollback(element, originalSectionId) {
    const originalContainer = document.querySelector(`[data-sortable-section-id-value="${originalSectionId}"]`)
    if (originalContainer) {
      originalContainer.appendChild(element)
    }
  }

  showToast(message, type) {
    const event = new CustomEvent('toast:show', {
      detail: { title: message, type },
      bubbles: true
    })
    document.dispatchEvent(event)
  }

  updateCounters(oldSectionId, newSectionId) {
    this.updateCounter(oldSectionId, -1)
    this.updateCounter(newSectionId, 1)
  }

  updateCounter(sectionId, delta) {
    const sectionCard = document.querySelector(`#card_evaluation_section_${sectionId}`) ||
                        document.querySelector(`#card_form_section_${sectionId}`)
    if (!sectionCard) return

    const footer = sectionCard.querySelector('.card-footer')
    if (!footer) return

    const match = footer.textContent.match(/(\d+)/)
    if (match) {
      const currentCount = parseInt(match[1], 10)
      const newCount = Math.max(0, currentCount + delta)
      footer.textContent = footer.textContent.replace(/\d+/, newCount)
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
